import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Container, Row, Col, Tab, Tabs, Table, Button, Form, Modal, Badge, InputGroup, Spinner } from 'react-bootstrap';
import { Pencil, Trash, Eye, Plus, ClipboardData, Download, BoxArrowUpRight, Search, ArrowUp, ArrowDown, Envelope, Upload } from 'react-bootstrap-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SurveyService from "../../services/survey.service";
import CatalogService from "../../services/catalog.service";
import {
    VALIDATION_EMAIL_REGEX,
    VALIDATION_PHONE_REGEX,
    buildAllowedCharsRegex,
    detectValidationPreset
} from "../../util/surveyValidation";

import { getUserCapabilities, getSubsite } from '../../reducers/auth';
import { resolveSwatchUrl } from '../../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_LOCALHOST_URL;

const FIELD_TYPE_LABELS = {
    string: 'Text',
    number: 'Number',
    date: 'Date',
    radio: 'Radio (Multiple Choice)',
    product: 'Product Selection',
    address: 'Address',
    image: 'Image with Caption',
    gallery: 'Image Gallery',
    statictext: 'Static Text (WYSIWYG)'
};

const AdminSurveys = () => {
    const [surveys, setSurveys] = useState([]);
    const [fields, setFields] = useState([]);
    const [showSurveyModal, setShowSurveyModal] = useState(false);
    const [showFieldModal, setShowFieldModal] = useState(false);
    const [showFieldsModal, setShowFieldsModal] = useState(false);
    const [showResponsesModal, setShowResponsesModal] = useState(false);
    const [currentSurvey, setCurrentSurvey] = useState(null);
    const [currentField, setCurrentField] = useState(null);
    const [responses, setResponses] = useState([]);

    // Form state
    const [surveyTitle, setSurveyTitle] = useState('');
    const [surveyDescription, setSurveyDescription] = useState('');
    const [surveyActive, setSurveyActive] = useState(true);
    const [surveyCode, setSurveyCode] = useState('');
    const [surveyReportEmails, setSurveyReportEmails] = useState('');
    const [surveyReportFrequency, setSurveyReportFrequency] = useState(1);
    const [surveyReportTimezone, setSurveyReportTimezone] = useState('America/New_York');
    const [surveyStartDate, setSurveyStartDate] = useState('');
    const [surveyEndDate, setSurveyEndDate] = useState('');
    const [surveyProductImageSide, setSurveyProductImageSide] = useState('right');
    const [reportSchedules, setReportSchedules] = useState({});

    const userCapabilities = useSelector(getUserCapabilities);
    const { isAdmin, isSuperAdmin } = useSelector(state => state.auth);
    const currentSubsite = useSelector(getSubsite);
    const hasViewSurveyCapability = userCapabilities?.some(c => c.capabilityName === "VIEW_SURVEY");
    const isReadOnly = !isAdmin && !isSuperAdmin && hasViewSurveyCapability;


    // Common US timezones
    const timezones = [
        { value: 'America/New_York', label: 'Eastern Time (ET)' },
        { value: 'America/Chicago', label: 'Central Time (CT)' },
        { value: 'America/Denver', label: 'Mountain Time (MT)' },
        { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
        { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
        { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
        { value: 'UTC', label: 'UTC' }
    ];

    const [fieldTitle, setFieldTitle] = useState('');
    const [fieldType, setFieldType] = useState('string');
    const [fieldLength, setFieldLength] = useState(255);
    const [fieldValidation, setFieldValidation] = useState('');
    const [fieldValidationPreset, setFieldValidationPreset] = useState('none');
    const [fieldValidationAllowedChars, setFieldValidationAllowedChars] = useState('');
    const [fieldValidationUppercase, setFieldValidationUppercase] = useState(true);
    const [fieldValidationLowercase, setFieldValidationLowercase] = useState(true);
    const [fieldValidationDigits, setFieldValidationDigits] = useState(true);
    const [fieldMask, setFieldMask] = useState('');
    const [fieldRequired, setFieldRequired] = useState(false);
    const [fieldOptions, setFieldOptions] = useState('');
    const [fieldAddressType, setFieldAddressType] = useState('manual');
    const [fieldRejectPoBox, setFieldRejectPoBox] = useState(false);
    const [fieldSelectType, setFieldSelectType] = useState('radio');
    const [fieldGalleryLayout, setFieldGalleryLayout] = useState('horizontal');
    const [selectedFieldIds, setSelectedFieldIds] = useState([]);
    // Product configuration per field in the survey (keyed by field id)
    const [fieldProductConfigs, setFieldProductConfigs] = useState({});
    // Colors available for each product alternate: key `${fieldId}-${idx}` -> items[]
    const [productAltColors, setProductAltColors] = useState({});
    // Image configuration per field in the survey (keyed by field id)
    const [fieldImageConfigs, setFieldImageConfigs] = useState({});
    // Gallery configuration per field in the survey (keyed by field id)
    const [fieldGalleryConfigs, setFieldGalleryConfigs] = useState({});
    // Static text (WYSIWYG HTML) per field in the survey (keyed by field id)
    const [fieldStaticTextConfigs, setFieldStaticTextConfigs] = useState({});

    // Response edit state
    const [showEditResponseModal, setShowEditResponseModal] = useState(false);
    const [currentResponse, setCurrentResponse] = useState(null);
    const [editFieldValues, setEditFieldValues] = useState({});

    // Product search state
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);
    const [searchingForProduct, setSearchingForProduct] = useState(0); // index into alternateProducts array
    const [sendingReportId, setSendingReportId] = useState(null);
    const [uploadingImageFieldId, setUploadingImageFieldId] = useState(null);

    useEffect(() => {
        fetchSurveys();
        fetchFields();
        fetchReportSchedules();
    }, []);

    const fetchSurveys = async () => {
        try {
            const response = await SurveyService.getSurveys();
            setSurveys(response.data);
        } catch (err) {
            console.error("Error fetching surveys:", err);
        }
    };

    const fetchReportSchedules = async () => {
        try {
            const response = await SurveyService.getReportSchedules();
            setReportSchedules(response.data);
        } catch (err) {
            console.error("Error fetching report schedules:", err);
        }
    };

    const fetchFields = async () => {
        try {
            const response = await SurveyService.getFields();
            setFields(response.data);
        } catch (err) {
            console.error("Error fetching fields:", err);
        }
    };

    const sendReportNow = async (survey) => {
        // Read-only users (VIEW_SURVEY capability) always get the report sent
        // to their own address, regardless of the survey's configured recipients.
        const targetDescription = isReadOnly ? 'your email address' : survey.reportEmails;

        if (!isReadOnly && !survey.reportEmails) {
            alert("No report emails configured for this survey. Edit the survey to add email addresses.");
            return;
        }

        if (!window.confirm(`Send survey report to: ${targetDescription}?`)) {
            return;
        }

        setSendingReportId(survey.id);
        try {
            const response = await SurveyService.sendReportNow(survey.id);
            const recipients = response?.data?.recipients;
            const recipientText = Array.isArray(recipients) && recipients.length > 0
                ? recipients.join(', ')
                : (isReadOnly ? 'your email address' : (survey.reportEmails || 'configured recipients'));
            alert(`Report sent successfully to: ${recipientText}`);
        } catch (err) {
            console.error("Error sending report:", err);
            alert("Error sending report: " + (err.response?.data?.message || err.message));
        } finally {
            setSendingReportId(null);
        }
    };

    // Survey handlers
    const openSurveyModal = (survey = null) => {
        setCurrentSurvey(survey);
        setSurveyTitle(survey?.title || '');
        setSurveyDescription(survey?.description || '');
        setSurveyActive(survey?.active !== false);
        setSurveyCode(survey?.code || '');
        setSurveyReportEmails(survey?.reportEmails || '');
        setSurveyReportFrequency(survey?.reportFrequency || 1);
        setSurveyReportTimezone(survey?.reportTimezone || 'America/New_York');
        setSurveyStartDate(survey?.startDate || '');
        setSurveyEndDate(survey?.endDate || '');
        setSurveyProductImageSide(survey?.productImageSide === 'left' ? 'left' : 'right');
        setShowSurveyModal(true);
    };

    const saveSurvey = async () => {
        try {
            const data = {
                title: surveyTitle,
                description: surveyDescription,
                active: surveyActive,
                reportEmails: surveyReportEmails || null,
                reportFrequency: surveyReportFrequency || 1,
                reportTimezone: surveyReportTimezone || 'America/New_York',
                startDate: surveyStartDate || null,
                endDate: surveyEndDate || null,
                productImageSide: surveyProductImageSide === 'left' ? 'left' : 'right'
            };
            if (currentSurvey) {
                // Include code when editing
                data.code = surveyCode;
                await SurveyService.updateSurvey(currentSurvey.id, data);
            } else {
                await SurveyService.createSurvey(data);
            }
            setShowSurveyModal(false);
            fetchSurveys();
        } catch (err) {
            console.error("Error saving survey:", err);
        }
    };

    const deleteSurvey = async (id) => {
        if (window.confirm("Are you sure you want to delete this survey?")) {
            await SurveyService.deleteSurvey(id);
            fetchSurveys();
        }
    };

    // Field handlers
    const openFieldModal = (field = null) => {
        setCurrentField(field);
        setFieldTitle(field?.title || '');
        setFieldType(field?.fieldType || 'string');
        setFieldLength(field?.length || 255);
        const validation = field?.validation || '';
        setFieldValidation(validation);
        const detected = detectValidationPreset(validation);
        setFieldValidationPreset(detected.preset);
        setFieldValidationAllowedChars(detected.allowedChars);
        // Restore exact toggle state for existing allowedchars fields; default to
        // all-on for everything else so switching to "Allowed Characters" starts
        // from a permissive baseline.
        setFieldValidationUppercase(detected.preset === 'allowedchars' ? detected.uppercase : true);
        setFieldValidationLowercase(detected.preset === 'allowedchars' ? detected.lowercase : true);
        setFieldValidationDigits(detected.preset === 'allowedchars' ? detected.digits : true);
        setFieldMask(field?.mask || '');
        setFieldRequired(field?.required || false);
        setFieldOptions(field?.options ? JSON.parse(field.options).join('\n') : '');
        setFieldAddressType(field?.addressType || 'manual');
        setFieldRejectPoBox(!!field?.rejectPoBox);
        setFieldSelectType(field?.selectType || 'radio');
        setFieldGalleryLayout(field?.galleryLayout || 'horizontal');
        setShowFieldModal(true);
    };

    const handleValidationPresetChange = (preset) => {
        setFieldValidationPreset(preset);
        if (preset === 'none') {
            setFieldValidation('');
        } else if (preset === 'email') {
            setFieldValidation(VALIDATION_EMAIL_REGEX);
        } else if (preset === 'phone') {
            setFieldValidation(VALIDATION_PHONE_REGEX);
        } else if (preset === 'allowedchars') {
            setFieldValidation(buildAllowedCharsRegex({
                uppercase: fieldValidationUppercase,
                lowercase: fieldValidationLowercase,
                digits: fieldValidationDigits,
                allowedChars: fieldValidationAllowedChars
            }));
        }
        // 'custom' keeps the existing regex value as-is
    };

    const handleAllowedCharsChange = (chars) => {
        setFieldValidationAllowedChars(chars);
        setFieldValidation(buildAllowedCharsRegex({
            uppercase: fieldValidationUppercase,
            lowercase: fieldValidationLowercase,
            digits: fieldValidationDigits,
            allowedChars: chars
        }));
    };

    const handleAllowedCategoryToggle = (category, checked) => {
        const next = {
            uppercase: fieldValidationUppercase,
            lowercase: fieldValidationLowercase,
            digits: fieldValidationDigits,
            allowedChars: fieldValidationAllowedChars
        };
        next[category] = checked;
        if (category === 'uppercase') setFieldValidationUppercase(checked);
        if (category === 'lowercase') setFieldValidationLowercase(checked);
        if (category === 'digits') setFieldValidationDigits(checked);
        setFieldValidation(buildAllowedCharsRegex(next));
    };

    const saveField = async () => {
        try {
            const options = fieldType === 'radio' ? fieldOptions.split('\n').filter(o => o.trim()) : null;
            const data = {
                title: fieldTitle,
                fieldType,
                length: fieldLength,
                validation: fieldValidation,
                mask: fieldType === 'string' ? (fieldMask || null) : null,
                required: fieldRequired,
                options,
                addressType: fieldType === 'address' ? fieldAddressType : null,
                rejectPoBox: fieldType === 'address' ? !!fieldRejectPoBox : false,
                selectType: fieldType === 'radio' ? fieldSelectType : null
            };
            if (currentField) {
                await SurveyService.updateField(currentField.id, data);
            } else {
                await SurveyService.createField(data);
            }
            setShowFieldModal(false);
            fetchFields();
        } catch (err) {
            console.error("Error saving field:", err);
        }
    };

    const deleteField = async (id) => {
        if (window.confirm("Are you sure you want to delete this field?")) {
            await SurveyService.deleteField(id);
            fetchFields();
        }
    };

    // Survey fields management
    const altColorKey = (fieldId, idx) => `${fieldId}-${idx}`;

    const loadAltColors = async (fieldId, idx, styleId, subsiteId) => {
        const key = altColorKey(fieldId, idx);
        if (!styleId) {
            setProductAltColors(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            return;
        }
        try {
            const response = await SurveyService.getProductForSurvey(styleId, subsiteId);
            setProductAltColors(prev => ({
                ...prev,
                [key]: response.data?.items || []
            }));
        } catch (err) {
            console.error("Error loading product colors:", err);
            setProductAltColors(prev => ({ ...prev, [key]: [] }));
        }
    };

    const openFieldsModal = (survey) => {
        setCurrentSurvey(survey);
        setSelectedFieldIds(survey.survey_fields?.map(f => f.id) || []);
        // Initialize product configs from the junction table data
        const productConfigs = {};
        const imageConfigs = {};
        const galleryConfigs = {};
        const staticTextConfigs = {};
        const colorLoads = [];
        const subsiteId = survey.subsiteId || currentSubsite?.id;
        survey.survey_fields?.forEach(f => {
            const junctionData = f.survey_survey_fields || {};
            if (f.fieldType === 'product') {
                let alternateProducts = [];
                if (junctionData.alternateProducts) {
                    // New format
                    const parsed = typeof junctionData.alternateProducts === 'string'
                        ? JSON.parse(junctionData.alternateProducts)
                        : junctionData.alternateProducts;
                    alternateProducts = parsed || [];
                } else if (junctionData.styleId) {
                    // Legacy format — convert to new format
                    alternateProducts = [{ styleId: junctionData.styleId, label: junctionData.styleIdLabel || 'Option 1', title: '' }];
                    if (junctionData.styleId2) {
                        alternateProducts.push({ styleId: junctionData.styleId2, label: junctionData.styleId2Label || 'Option 2', title: '' });
                    }
                }
                productConfigs[f.id] = { alternateProducts };
                alternateProducts.forEach((alt, idx) => {
                    if (alt.styleId) colorLoads.push({ fieldId: f.id, idx, styleId: alt.styleId });
                });
            }
            if (f.fieldType === 'image') {
                imageConfigs[f.id] = {
                    imageUrl: junctionData.imageUrl || '',
                    caption: junctionData.caption || ''
                };
            }
            if (f.fieldType === 'gallery') {
                let images = [];
                if (junctionData.galleryImages) {
                    const parsed = typeof junctionData.galleryImages === 'string'
                        ? JSON.parse(junctionData.galleryImages)
                        : junctionData.galleryImages;
                    // Handle both old format (array of URLs) and new format (array of {url, caption})
                    images = parsed.map(img => typeof img === 'string' ? { url: img, caption: '' } : img);
                }
                galleryConfigs[f.id] = {
                    images,
                    layout: junctionData.galleryLayout || 'horizontal'
                };
            }
            if (f.fieldType === 'statictext') {
                staticTextConfigs[f.id] = { html: junctionData.staticText || '' };
            }
        });
        setFieldProductConfigs(productConfigs);
        setFieldImageConfigs(imageConfigs);
        setFieldGalleryConfigs(galleryConfigs);
        setFieldStaticTextConfigs(staticTextConfigs);
        setProductAltColors({});
        setProductSearchTerm('');
        setProductSearchResults([]);
        setSearchingForProduct(0);
        setEditingProductFieldId(null);
        setShowFieldsModal(true);
        colorLoads.forEach(({ fieldId, idx, styleId }) => loadAltColors(fieldId, idx, styleId, subsiteId));
    };

    const toggleFieldSelection = (fieldId) => {
        setSelectedFieldIds(prev => {
            const isSelected = prev.includes(fieldId);
            if (isSelected) {
                // Remove field and its configs
                setFieldProductConfigs(pc => {
                    const newPc = { ...pc };
                    delete newPc[fieldId];
                    return newPc;
                });
                setFieldImageConfigs(ic => {
                    const newIc = { ...ic };
                    delete newIc[fieldId];
                    return newIc;
                });
                setFieldGalleryConfigs(gc => {
                    const newGc = { ...gc };
                    delete newGc[fieldId];
                    return newGc;
                });
                setFieldStaticTextConfigs(sc => {
                    const newSc = { ...sc };
                    delete newSc[fieldId];
                    return newSc;
                });
                return prev.filter(id => id !== fieldId);
            } else {
                // Add field and initialize config if needed
                const field = fields.find(f => f.id === fieldId);
                if (field?.fieldType === 'product') {
                    setFieldProductConfigs(pc => ({
                        ...pc,
                        [fieldId]: { alternateProducts: [] }
                    }));
                }
                if (field?.fieldType === 'image') {
                    setFieldImageConfigs(ic => ({
                        ...ic,
                        [fieldId]: {
                            imageUrl: '',
                            caption: ''
                        }
                    }));
                }
                if (field?.fieldType === 'gallery') {
                    setFieldGalleryConfigs(gc => ({
                        ...gc,
                        [fieldId]: {
                            images: [],
                            layout: 'horizontal'
                        }
                    }));
                }
                if (field?.fieldType === 'statictext') {
                    setFieldStaticTextConfigs(sc => ({
                        ...sc,
                        [fieldId]: { html: '' }
                    }));
                }
                return [...prev, fieldId];
            }
        });
    };

    const moveFieldUp = (fieldId) => {
        setSelectedFieldIds(prev => {
            const index = prev.indexOf(fieldId);
            if (index <= 0) return prev;
            const newArr = [...prev];
            [newArr[index - 1], newArr[index]] = [newArr[index], newArr[index - 1]];
            return newArr;
        });
    };

    const moveFieldDown = (fieldId) => {
        setSelectedFieldIds(prev => {
            const index = prev.indexOf(fieldId);
            if (index < 0 || index >= prev.length - 1) return prev;
            const newArr = [...prev];
            [newArr[index], newArr[index + 1]] = [newArr[index + 1], newArr[index]];
            return newArr;
        });
    };

    const saveFieldSelection = async () => {
        // Build field configs array with product, image, and gallery configuration
        const fieldConfigs = selectedFieldIds.map(fieldId => {
            const field = fields.find(f => f.id === fieldId);
            const config = { fieldId };

            // Add product config if applicable
            if (field?.fieldType === 'product' && fieldProductConfigs[fieldId]) {
                config.alternateProducts = JSON.stringify(fieldProductConfigs[fieldId].alternateProducts || []);
            }

            // Add image config if applicable
            if (field?.fieldType === 'image' && fieldImageConfigs[fieldId]) {
                config.imageUrl = fieldImageConfigs[fieldId].imageUrl;
                config.caption = fieldImageConfigs[fieldId].caption;
            }

            // Add gallery config if applicable
            if (field?.fieldType === 'gallery' && fieldGalleryConfigs[fieldId]) {
                config.galleryImages = JSON.stringify(fieldGalleryConfigs[fieldId].images || []);
                config.galleryLayout = fieldGalleryConfigs[fieldId].layout || 'horizontal';
            }

            // Add static text config if applicable
            if (field?.fieldType === 'statictext' && fieldStaticTextConfigs[fieldId]) {
                config.staticText = fieldStaticTextConfigs[fieldId].html || '';
            }

            return config;
        });
        await SurveyService.updateSurveyFields(currentSurvey.id, fieldConfigs);
        setShowFieldsModal(false);
        fetchSurveys();
    };

    // Product search for manage fields modal
    const [editingProductFieldId, setEditingProductFieldId] = useState(null);

    const searchProducts = async () => {
        if (!productSearchTerm.trim()) return;
        setSearchingProducts(true);
        try {
            const result = await CatalogService.searchCatalog([productSearchTerm, 1, 20]);
            setProductSearchResults(result?.data || []);
        } catch (err) {
            console.error("Error searching products:", err);
        }
        setSearchingProducts(false);
    };

    const selectProduct = (product) => {
        if (!editingProductFieldId) return;
        const idx = searchingForProduct;
        const fieldId = editingProductFieldId;
        setFieldProductConfigs(prev => {
            const newAlts = [...(prev[fieldId]?.alternateProducts || [])];
            if (idx >= 0 && idx < newAlts.length) {
                newAlts[idx] = {
                    ...newAlts[idx],
                    styleId: product.styleId,
                    title: product.title,
                    featuredColorId: null
                };
            }
            return { ...prev, [fieldId]: { alternateProducts: newAlts } };
        });
        setProductSearchResults([]);
        setProductSearchTerm('');
        const subsiteId = currentSurvey?.subsiteId || currentSubsite?.id;
        loadAltColors(fieldId, idx, product.styleId, subsiteId);
    };

    const removeAlternateProduct = (fieldId, idx) => {
        setFieldProductConfigs(prev => {
            const newAlts = (prev[fieldId]?.alternateProducts || []).filter((_, i) => i !== idx);
            return { ...prev, [fieldId]: { alternateProducts: newAlts } };
        });
        setProductAltColors(prev => {
            const next = { ...prev };
            // Drop colors for removed index and re-key later indices
            const keys = Object.keys(next).filter(k => k.startsWith(`${fieldId}-`));
            keys.forEach(k => delete next[k]);
            const remaining = (fieldProductConfigs[fieldId]?.alternateProducts || []).filter((_, i) => i !== idx);
            remaining.forEach((alt, newIdx) => {
                const oldIdx = newIdx >= idx ? newIdx + 1 : newIdx;
                const oldKey = altColorKey(fieldId, oldIdx);
                if (prev[oldKey]) next[altColorKey(fieldId, newIdx)] = prev[oldKey];
            });
            return next;
        });
    };

    const setFeaturedColor = (fieldId, idx, colorId) => {
        setFieldProductConfigs(prev => {
            const newAlts = [...(prev[fieldId]?.alternateProducts || [])];
            if (idx < 0 || idx >= newAlts.length) return prev;
            const current = newAlts[idx].featuredColorId;
            newAlts[idx] = {
                ...newAlts[idx],
                featuredColorId: current === colorId ? null : colorId
            };
            return { ...prev, [fieldId]: { alternateProducts: newAlts } };
        });
    };

    const addAlternateProduct = (fieldId) => {
        setFieldProductConfigs(prev => {
            const newAlts = [...(prev[fieldId]?.alternateProducts || []), { styleId: null, label: '', title: '' }];
            return { ...prev, [fieldId]: { alternateProducts: newAlts } };
        });
    };

    // Image upload for image fields
    const handleImageUpload = async (fieldId, file) => {
        if (!file) return;
        setUploadingImageFieldId(fieldId);
        try {
            const response = await SurveyService.uploadImage(file);
            const imageUrl = response.data.imageUrl;
            setFieldImageConfigs(prev => ({
                ...prev,
                [fieldId]: { ...prev[fieldId], imageUrl }
            }));
        } catch (err) {
            console.error("Error uploading image:", err);
            alert("Failed to upload image. Please try again.");
        }
        setUploadingImageFieldId(null);
    };

    const clearImageSelection = (fieldId) => {
        setFieldImageConfigs(prev => ({
            ...prev,
            [fieldId]: { ...prev[fieldId], imageUrl: '' }
        }));
    };

    // Gallery image upload
    const [uploadingGalleryFieldId, setUploadingGalleryFieldId] = useState(null);

    const handleGalleryImageUpload = async (fieldId, file) => {
        if (!file) return;
        setUploadingGalleryFieldId(fieldId);
        try {
            const response = await SurveyService.uploadImage(file);
            const imageUrl = response.data.imageUrl;
            setFieldGalleryConfigs(prev => ({
                ...prev,
                [fieldId]: {
                    ...prev[fieldId],
                    images: [...(prev[fieldId]?.images || []), { url: imageUrl, caption: '' }]
                }
            }));
        } catch (err) {
            console.error("Error uploading gallery image:", err);
            alert("Failed to upload image. Please try again.");
        }
        setUploadingGalleryFieldId(null);
    };

    const removeGalleryImage = (fieldId, index) => {
        setFieldGalleryConfigs(prev => ({
            ...prev,
            [fieldId]: {
                ...prev[fieldId],
                images: prev[fieldId]?.images?.filter((_, i) => i !== index) || []
            }
        }));
    };

    const updateGalleryImageCaption = (fieldId, index, caption) => {
        setFieldGalleryConfigs(prev => ({
            ...prev,
            [fieldId]: {
                ...prev[fieldId],
                images: prev[fieldId]?.images?.map((img, i) =>
                    i === index ? { ...img, caption } : img
                ) || []
            }
        }));
    };

    // Responses
    const viewResponses = async (survey) => {
        setCurrentSurvey(survey);
        const response = await SurveyService.getResponses(survey.id);
        setResponses(response.data);
        setShowResponsesModal(true);
    };

    const getSurveyUrl = (code) => {
        return `${window.location.origin}/survey/${code}`;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert("Copied to clipboard!");
    };

    const downloadResponsesExcel = async () => {
        if (!currentSurvey || responses.length === 0) return;
        try {
            const response = await SurveyService.downloadResponses(currentSurvey.id);
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const today = new Date().toISOString().split('T')[0];
            link.download = `${currentSurvey.title.replace(/[^a-zA-Z0-9]/g, '_')}_responses_${today}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading responses:", err);
            alert("Failed to download responses.");
        }
    };

    // Response editing
    const openEditResponseModal = (response) => {
        setCurrentResponse(response);
        // Initialize edit values from current response fields
        const values = {};
        currentSurvey?.survey_fields?.forEach(field => {
            const rf = response.survey_response_fields?.find(rf => rf.surveyFieldId === field.id);
            values[field.id] = rf?.value || '';
        });
        setEditFieldValues(values);
        setShowEditResponseModal(true);
    };

    const saveResponseEdit = async () => {
        try {
            await SurveyService.updateResponse(currentResponse.id, editFieldValues);
            setShowEditResponseModal(false);
            // Refresh responses
            const response = await SurveyService.getResponses(currentSurvey.id);
            setResponses(response.data);
        } catch (err) {
            console.error("Error saving response:", err);
        }
    };

    // Computes a survey's effective availability based on active flag + start/end dates.
    // Returns { variant, label, detail } for badge display.
    const getSurveyStatus = (survey) => {
        if (!survey.active) {
            return { variant: 'secondary', label: 'Inactive' };
        }
        const today = new Date().toISOString().slice(0, 10);
        if (survey.startDate && today < survey.startDate) {
            return { variant: 'info', label: 'Scheduled', detail: `Opens ${survey.startDate}` };
        }
        if (survey.endDate && today > survey.endDate) {
            return { variant: 'dark', label: 'Ended', detail: `Closed ${survey.endDate}` };
        }
        const detailParts = [];
        if (survey.startDate) detailParts.push(`from ${survey.startDate}`);
        if (survey.endDate) detailParts.push(`until ${survey.endDate}`);
        return { variant: 'success', label: 'Active', detail: detailParts.join(' ') || undefined };
    };

    const deleteResponseHandler = async (responseId) => {
        if (window.confirm("Are you sure you want to delete this response?")) {
            try {
                await SurveyService.deleteResponse(responseId);
                // Refresh responses
                const response = await SurveyService.getResponses(currentSurvey.id);
                setResponses(response.data);
            } catch (err) {
                console.error("Error deleting response:", err);
            }
        }
    };

    return (
        <Container>
            <h2>Surveys</h2>
            <Tabs defaultActiveKey="surveys" className="mb-3">
                <Tab eventKey="surveys" title="Surveys">
                    {!isReadOnly && (
                        <Button variant="primary" className="mb-3" onClick={() => openSurveyModal()}>
                            <Plus /> New Survey
                        </Button>
                    )}
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Code</th>
                                <th>View</th>
                                <th>Fields</th>
                                <th>Responses</th>
                                <th>Report</th>
                                <th>Status</th>
                                {!isReadOnly && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {surveys.map(survey => (
                                <tr key={survey.id}>
                                    <td>{survey.title}</td>
                                    <td>
                                        <code>{survey.code}</code>
                                        <Button variant="link" size="sm" onClick={() => copyToClipboard(getSurveyUrl(survey.code))}>
                                            <ClipboardData />
                                        </Button>
                                    </td>
                                    <td>
                                        <Button variant="link" size="sm" onClick={() => window.open(getSurveyUrl(survey.code), '_blank')}>
                                            <BoxArrowUpRight /> Open
                                        </Button>
                                    </td>
                                    <td>
                                        <Badge bg="secondary">{survey.survey_fields?.length || 0}</Badge>
                                        {!isReadOnly && (
                                            <Button variant="link" size="sm" onClick={() => openFieldsModal(survey)}>Manage</Button>
                                        )}
                                    </td>
                                    <td>
                                        <Button variant="link" size="sm" onClick={() => viewResponses(survey)}>
                                            <Eye /> View
                                        </Button>
                                    </td>
                                    <td>
                                        {(survey.reportEmails || isReadOnly) ? (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                onClick={() => sendReportNow(survey)}
                                                disabled={sendingReportId === survey.id}
                                            >
                                                <Envelope /> {sendingReportId === survey.id ? 'Sending...' : 'Send Now'}
                                            </Button>
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {(() => {
                                            const status = getSurveyStatus(survey);
                                            return (
                                                <>
                                                    <Badge bg={status.variant}>{status.label}</Badge>
                                                    {status.detail && (
                                                        <div style={{ fontSize: '0.75em', color: '#666', marginTop: 2 }}>
                                                            {status.detail}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </td>
                                    {!isReadOnly && (
                                        <td>
                                            <Button variant="link" size="sm" onClick={() => openSurveyModal(survey)}><Pencil /></Button>
                                            <Button variant="link" size="sm" className="text-danger" onClick={() => deleteSurvey(survey.id)}><Trash /></Button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Tab>
                {!isReadOnly && (
                    <Tab eventKey="fields" title="Fields">
                        <Button variant="primary" className="mb-3" onClick={() => openFieldModal()}>
                            <Plus /> New Field
                        </Button>
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Type</th>
                                    <th>Product</th>
                                    <th>Required</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map(field => (
                                    <tr key={field.id}>
                                        <td>{field.title}</td>
                                        <td><Badge bg="info">{field.fieldType}</Badge></td>
                                        <td>{field.fieldType === 'product' && field.style ? field.style.title : '-'}</td>
                                        <td>{field.required ? "Yes" : "No"}</td>
                                        <td>
                                            <Button variant="link" size="sm" onClick={() => openFieldModal(field)}><Pencil /></Button>
                                            <Button variant="link" size="sm" className="text-danger" onClick={() => deleteField(field.id)}><Trash /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Tab>
                )}
            </Tabs>

            {/* Survey Modal */}
            <Modal show={showSurveyModal} onHide={() => setShowSurveyModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{currentSurvey ? 'Edit Survey' : 'New Survey'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control type="text" value={surveyTitle} onChange={(e) => setSurveyTitle(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control as="textarea" rows={3} value={surveyDescription} onChange={(e) => setSurveyDescription(e.target.value)} />
                    </Form.Group>
                    {currentSurvey && (
                        <Form.Group className="mb-3">
                            <Form.Label>Code</Form.Label>
                            <Form.Control
                                type="text"
                                value={surveyCode}
                                onChange={(e) => setSurveyCode(e.target.value.toUpperCase())}
                                maxLength={30}
                                style={{ fontFamily: 'monospace' }}
                            />
                            <Form.Text className="text-muted">
                                Up to 30-character code used in the survey URL
                            </Form.Text>
                        </Form.Group>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Check type="checkbox" label="Active" checked={surveyActive} onChange={(e) => setSurveyActive(e.target.checked)} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Product Preview Side</Form.Label>
                        <Form.Select
                            value={surveyProductImageSide}
                            onChange={(e) => setSurveyProductImageSide(e.target.value)}
                        >
                            <option value="right">Right</option>
                            <option value="left">Left</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Which side the product preview images appear on for respondents (desktop).
                        </Form.Text>
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Start Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={surveyStartDate || ''}
                                    onChange={(e) => setSurveyStartDate(e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    Leave blank for no start bound. Survey opens at the start of this day.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>End Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={surveyEndDate || ''}
                                    onChange={(e) => setSurveyEndDate(e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                    Leave blank for no end bound. Survey closes at the end of this day.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Report Emails</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={surveyReportEmails}
                            onChange={(e) => setSurveyReportEmails(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                        />
                        <Form.Text className="text-muted">
                            Comma-separated list of email addresses to receive survey response reports.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Report Frequency</Form.Label>
                        <Form.Select
                            value={surveyReportFrequency}
                            onChange={(e) => setSurveyReportFrequency(parseInt(e.target.value))}
                        >
                            <option value={1}>1x per day</option>
                            <option value={2}>2x per day</option>
                            <option value={3}>3x per day</option>
                            <option value={4}>4x per day</option>
                            <option value={5}>5x per day</option>
                            <option value={6}>6x per day</option>
                        </Form.Select>
                        {reportSchedules[surveyReportFrequency] && (
                            <Form.Text className="text-muted">
                                Reports will be sent at: {reportSchedules[surveyReportFrequency].join(', ')}
                            </Form.Text>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Report Timezone</Form.Label>
                        <Form.Select
                            value={surveyReportTimezone}
                            onChange={(e) => setSurveyReportTimezone(e.target.value)}
                        >
                            {timezones.map(tz => (
                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Report times are based on this timezone.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSurveyModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={saveSurvey}>Save</Button>
                </Modal.Footer>
            </Modal>

            {/* Field Modal */}
            <Modal show={showFieldModal} onHide={() => setShowFieldModal(false)} size={fieldType === 'product' ? 'lg' : undefined}>
                <Modal.Header closeButton>
                    <Modal.Title>{currentField ? 'Edit Field' : 'New Field'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control type="text" value={fieldTitle} onChange={(e) => setFieldTitle(e.target.value)} />
                    </Form.Group>
                    {currentField ? (
                        <Form.Group className="mb-3">
                            <Form.Label>Type</Form.Label>
                            <Form.Control type="text" value={FIELD_TYPE_LABELS[fieldType] || fieldType} disabled readOnly />
                        </Form.Group>
                    ) : (
                        <Form.Group className="mb-3">
                            <Form.Label>Type</Form.Label>
                            <Form.Select value={fieldType} onChange={(e) => setFieldType(e.target.value)}>
                                <option value="string">Text</option>
                                <option value="number">Number</option>
                                <option value="date">Date</option>
                                <option value="radio">Radio (Multiple Choice)</option>
                                <option value="product">Product Selection</option>
                                <option value="address">Address</option>
                                <option value="image">Image with Caption</option>
                                <option value="gallery">Image Gallery</option>
                                <option value="statictext">Static Text (WYSIWYG)</option>
                            </Form.Select>
                        </Form.Group>
                    )}
                    {fieldType !== 'radio' && fieldType !== 'product' && fieldType !== 'address' && fieldType !== 'image' && fieldType !== 'gallery' && fieldType !== 'statictext' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Max Length</Form.Label>
                            <Form.Control type="number" value={fieldLength} onChange={(e) => setFieldLength(parseInt(e.target.value))} />
                        </Form.Group>
                    )}
                    {fieldType === 'string' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Input Mask</Form.Label>
                            <Form.Control
                                type="text"
                                value={fieldMask}
                                onChange={(e) => setFieldMask(e.target.value)}
                                placeholder="e.g. (999) 999-9999"
                                style={{ fontFamily: 'monospace' }}
                            />
                            <Form.Text className="text-muted">
                                Optional. Forces a fixed character layout as the respondent types.
                                Use <code>9</code> for a digit, <code>A</code> for a letter, <code>*</code> for either,
                                and any other character as a literal that's auto-inserted (e.g. <code>(999) 999-9999</code>,
                                <code> 999-99-9999</code>, <code> 99/99/9999</code>).
                            </Form.Text>
                        </Form.Group>
                    )}
                    {fieldType === 'address' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Address Input Type</Form.Label>
                                <Form.Select value={fieldAddressType} onChange={(e) => setFieldAddressType(e.target.value)}>
                                    <option value="manual">Manual Entry (user fills in address)</option>
                                    <option value="select">Select from List (predefined addresses)</option>
                                </Form.Select>
                                {fieldAddressType === 'select' && (
                                    <Form.Text className="text-muted">
                                        Addresses are managed in the subsite settings.
                                    </Form.Text>
                                )}
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    id="field-reject-po-box"
                                    label="Reject PO Boxes"
                                    checked={fieldRejectPoBox}
                                    onChange={(e) => setFieldRejectPoBox(e.target.checked)}
                                />
                                <Form.Text className="text-muted">
                                    Block addresses that look like a PO Box (e.g. &quot;PO Box&quot;, &quot;P.O. Box&quot;, &quot;Post Office Box&quot;).
                                </Form.Text>
                            </Form.Group>
                        </>
                    )}
                    {fieldType === 'radio' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Selection Type</Form.Label>
                                <Form.Select value={fieldSelectType} onChange={(e) => setFieldSelectType(e.target.value)}>
                                    <option value="radio">Radio Buttons (single selection)</option>
                                    <option value="select">Dropdown (single selection)</option>
                                    <option value="multi">Checkboxes (multiple selections)</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Options (one per line)</Form.Label>
                                <Form.Control as="textarea" rows={4} value={fieldOptions} onChange={(e) => setFieldOptions(e.target.value)} />
                            </Form.Group>
                        </>
                    )}
                    {fieldType === 'product' && (
                        <Form.Text className="text-muted d-block mb-3">
                            Product configuration is done when adding this field to a survey. Use the "Manage" button on a survey to configure which products to use.
                        </Form.Text>
                    )}
                    {fieldType === 'image' && (
                        <Form.Text className="text-muted d-block mb-3">
                            Image and caption configuration is done when adding this field to a survey. Use the "Manage" button on a survey to upload an image and set the caption.
                        </Form.Text>
                    )}
                    {fieldType === 'gallery' && (
                        <Form.Text className="text-muted d-block mb-3">
                            Gallery images and layout are configured when adding this field to a survey. Use the "Manage" button on a survey to upload images and set the layout.
                        </Form.Text>
                    )}
                    {fieldType === 'statictext' && (
                        <Form.Text className="text-muted d-block mb-3">
                            The static text content is entered when adding this field to a survey. Use the "Manage" button on a survey to author the text with the rich-text editor.
                        </Form.Text>
                    )}
                    {fieldType !== 'product' && fieldType !== 'image' && fieldType !== 'gallery' && fieldType !== 'statictext' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Validation</Form.Label>
                                <Form.Select
                                    value={fieldValidationPreset}
                                    onChange={(e) => handleValidationPresetChange(e.target.value)}
                                >
                                    <option value="none">None</option>
                                    <option value="email">Email Address</option>
                                    <option value="phone">Phone Number</option>
                                    <option value="allowedchars">Allowed Characters</option>
                                    <option value="custom">Custom (regex pattern)</option>
                                </Form.Select>
                            </Form.Group>
                            {fieldValidationPreset === 'allowedchars' && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Allowed Character Sets</Form.Label>
                                        <Form.Check
                                            type="checkbox"
                                            id="field-validation-uppercase"
                                            label="Uppercase letters (A-Z)"
                                            checked={fieldValidationUppercase}
                                            onChange={(e) => handleAllowedCategoryToggle('uppercase', e.target.checked)}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="field-validation-lowercase"
                                            label="Lowercase letters (a-z)"
                                            checked={fieldValidationLowercase}
                                            onChange={(e) => handleAllowedCategoryToggle('lowercase', e.target.checked)}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="field-validation-digits"
                                            label="Digits (0-9)"
                                            checked={fieldValidationDigits}
                                            onChange={(e) => handleAllowedCategoryToggle('digits', e.target.checked)}
                                        />
                                        <Form.Text className="text-muted">Spaces are always allowed.</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Other Allowed Characters</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={fieldValidationAllowedChars}
                                            onChange={(e) => handleAllowedCharsChange(e.target.value)}
                                            placeholder="e.g. -_.@"
                                        />
                                        <Form.Text className="text-muted">
                                            List any additional special characters to permit (e.g. punctuation).
                                        </Form.Text>
                                    </Form.Group>
                                </>
                            )}
                            {fieldValidationPreset !== 'none' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Validation Pattern (regex)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={fieldValidation}
                                        onChange={(e) => setFieldValidation(e.target.value)}
                                        readOnly={fieldValidationPreset !== 'custom'}
                                        placeholder="Optional"
                                    />
                                    {fieldValidationPreset !== 'custom' && (
                                        <Form.Text className="text-muted">
                                            Generated from the preset above. Choose "Custom" to edit directly.
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            )}
                        </>
                    )}
                    {fieldType !== 'image' && fieldType !== 'gallery' && fieldType !== 'statictext' && (
                        <Form.Group className="mb-3">
                            <Form.Check type="checkbox" label="Required" checked={fieldRequired} onChange={(e) => setFieldRequired(e.target.checked)} />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFieldModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={saveField}>Save</Button>
                </Modal.Footer>
            </Modal>

            {/* Manage Fields Modal */}
            <Modal show={showFieldsModal} onHide={() => setShowFieldsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Manage Survey Fields</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Selected Fields with Reorder */}
                    {selectedFieldIds.length > 0 && (
                        <>
                            <h6>Selected Fields (drag to reorder)</h6>
                            <div className="mb-4" style={{ background: '#e9ecef', padding: 10, borderRadius: 4 }}>
                                {selectedFieldIds.map((fieldId, index) => {
                                    const field = fields.find(f => f.id === fieldId);
                                    if (!field) return null;
                                    return (
                                        <div key={field.id} className="d-flex align-items-center mb-2 p-2" style={{ background: '#fff', borderRadius: 4, border: '1px solid #dee2e6' }}>
                                            <div className="me-2">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => moveFieldUp(fieldId)}
                                                    disabled={index === 0}
                                                    style={{ padding: '2px 6px' }}
                                                >
                                                    <ArrowUp />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => moveFieldDown(fieldId)}
                                                    disabled={index === selectedFieldIds.length - 1}
                                                    style={{ padding: '2px 6px', marginLeft: 2 }}
                                                >
                                                    <ArrowDown />
                                                </Button>
                                            </div>
                                            <div className="flex-grow-1">
                                                <strong>{index + 1}.</strong> {field.title} <Badge bg="info" className="ms-1">{field.fieldType}</Badge>
                                            </div>
                                            <Button variant="outline-danger" size="sm" onClick={() => toggleFieldSelection(fieldId)}>
                                                <Trash />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    <h6>Available Fields</h6>
                    <p className="text-muted small">Click to add fields to the survey:</p>
                    {fields.filter(f => !selectedFieldIds.includes(f.id)).map(field => (
                        <div key={field.id} className="mb-2 p-2" style={{ border: '1px solid #dee2e6', borderRadius: 4, cursor: 'pointer' }} onClick={() => toggleFieldSelection(field.id)}>
                            <Plus className="me-2 text-success" />
                            {field.title} <Badge bg="secondary" className="ms-1">{field.fieldType}</Badge>
                        </div>
                    ))}
                    {fields.filter(f => !selectedFieldIds.includes(f.id)).length === 0 && (
                        <p className="text-muted">All fields have been added to this survey.</p>
                    )}

                    {/* Product configuration for selected product fields */}
                    {selectedFieldIds.map(fieldId => {
                        const field = fields.find(f => f.id === fieldId);
                        if (!field || field.fieldType !== 'product') return null;
                        const alternates = fieldProductConfigs[fieldId]?.alternateProducts || [];
                        return (
                            <div key={`config-${field.id}`} className="mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: 4, border: '1px solid #dee2e6' }}>
                                <strong>Product Options: {field.title}</strong>
                                <p className="text-muted small mb-2 mt-1">Add one product per option. The survey taker will choose one.</p>

                                {alternates.map((alt, idx) => (
                                    <div key={idx} className="mb-2 p-2" style={{ background: '#fff', borderRadius: 4, border: '1px solid #dee2e6' }}>
                                        <Row className="align-items-center mb-1">
                                            <Col>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder={`Label (e.g. Men's, Women's)`}
                                                    value={alt.label || ''}
                                                    onChange={(e) => {
                                                        setFieldProductConfigs(prev => {
                                                            const newAlts = [...prev[field.id].alternateProducts];
                                                            newAlts[idx] = { ...newAlts[idx], label: e.target.value };
                                                            return { ...prev, [field.id]: { alternateProducts: newAlts } };
                                                        });
                                                    }}
                                                />
                                            </Col>
                                            <Col xs="auto">
                                                <Button variant="outline-danger" size="sm" onClick={() => removeAlternateProduct(field.id, idx)}>
                                                    <Trash />
                                                </Button>
                                            </Col>
                                        </Row>
                                        {alt.styleId ? (
                                            <div>
                                                <div className="d-flex align-items-center mb-2">
                                                    <Badge bg="success" className="me-2">{alt.title || `Style #${alt.styleId}`}</Badge>
                                                    <Button variant="link" size="sm" className="p-0" onClick={() => {
                                                        setFieldProductConfigs(prev => {
                                                            const newAlts = [...prev[field.id].alternateProducts];
                                                            newAlts[idx] = { ...newAlts[idx], styleId: null, title: '', featuredColorId: null };
                                                            return { ...prev, [field.id]: { alternateProducts: newAlts } };
                                                        });
                                                        setProductAltColors(prev => {
                                                            const next = { ...prev };
                                                            delete next[altColorKey(field.id, idx)];
                                                            return next;
                                                        });
                                                    }}>Change</Button>
                                                </div>
                                                <div className="small text-muted mb-1">Featured color (shown & selected by default):</div>
                                                <div>
                                                    {(productAltColors[altColorKey(field.id, idx)] || []).map(item => (
                                                        <div
                                                            key={item.colorId}
                                                            title={item.colorName}
                                                            onClick={() => setFeaturedColor(field.id, idx, item.colorId)}
                                                            style={{
                                                                marginRight: 6,
                                                                marginBottom: 4,
                                                                display: 'inline-block',
                                                                cursor: 'pointer',
                                                                border: alt.featuredColorId === item.colorId ? '3px solid #0d6efd' : '3px solid transparent',
                                                                borderRadius: 2
                                                            }}
                                                        >
                                                            {item.hexColor && item.hexColor !== "" && !(item.squareImageName && item.squareImageName !== "") && (
                                                                <div style={{ backgroundColor: item.hexColor, width: 28, height: 28 }} />
                                                            )}
                                                            {item.squareImageName && item.squareImageName !== "" && (
                                                                <img src={resolveSwatchUrl(item.squareImageName)} height={28} alt={item.colorName} />
                                                            )}
                                                        </div>
                                                    ))}
                                                    {alt.styleId && !(productAltColors[altColorKey(field.id, idx)] || []).length && (
                                                        <span className="text-muted small">Loading colors…</span>
                                                    )}
                                                </div>
                                                {alt.featuredColorId && (
                                                    <div className="small mt-1">
                                                        Featured: <strong>
                                                            {(productAltColors[altColorKey(field.id, idx)] || []).find(i => i.colorId === alt.featuredColorId)?.colorName
                                                                || `#${alt.featuredColorId}`}
                                                        </strong>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0 ms-2"
                                                            onClick={() => setFeaturedColor(field.id, idx, alt.featuredColorId)}
                                                        >
                                                            Clear
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <InputGroup size="sm">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search for a product..."
                                                    value={editingProductFieldId === field.id && searchingForProduct === idx ? productSearchTerm : ''}
                                                    onChange={(e) => { setEditingProductFieldId(field.id); setSearchingForProduct(idx); setProductSearchTerm(e.target.value); }}
                                                    onKeyDown={(e) => e.key === 'Enter' && searchProducts()}
                                                    onFocus={() => { setEditingProductFieldId(field.id); setSearchingForProduct(idx); }}
                                                />
                                                <Button variant="outline-secondary" onClick={() => { setEditingProductFieldId(field.id); setSearchingForProduct(idx); searchProducts(); }} disabled={searchingProducts}>
                                                    <Search />
                                                </Button>
                                            </InputGroup>
                                        )}
                                        {/* Search Results for this slot */}
                                        {editingProductFieldId === field.id && searchingForProduct === idx && productSearchResults.length > 0 && (
                                            <div className="mt-1" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                <Table striped bordered hover size="sm" className="mb-0">
                                                    <tbody>
                                                        {productSearchResults.map(product => (
                                                            <tr key={product.styleId}>
                                                                <td>{product.title} <code className="small">{product.code}</code></td>
                                                                <td style={{ width: 60 }}>
                                                                    <Button variant="primary" size="sm" onClick={() => selectProduct(product)}>Select</Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                <Button variant="outline-primary" size="sm" className="mt-1" onClick={() => addAlternateProduct(field.id)}>
                                    <Plus /> Add Product Option
                                </Button>
                            </div>
                        );
                    })}

                    {/* Image configuration for selected image fields */}
                    {selectedFieldIds.map(fieldId => {
                        const field = fields.find(f => f.id === fieldId);
                        if (!field || field.fieldType !== 'image') return null;
                        const imageUrl = fieldImageConfigs[field.id]?.imageUrl;
                        const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : BACKEND_URL + imageUrl) : null;
                        return (
                            <div key={`image-config-${field.id}`} className="mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: 4, border: '1px solid #dee2e6' }}>
                                <strong>Image Configuration: {field.title}</strong>
                                <div className="mt-2">
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Upload Image</Form.Label>
                                        {fullImageUrl ? (
                                            <div className="d-flex align-items-center mb-2">
                                                <div style={{ maxWidth: '150px', border: '1px solid #ddd', padding: '5px', borderRadius: '4px', marginRight: '10px' }}>
                                                    <img
                                                        src={fullImageUrl}
                                                        alt="Preview"
                                                        style={{ maxWidth: '100%', height: 'auto' }}
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                </div>
                                                <Button variant="outline-danger" size="sm" onClick={() => clearImageSelection(field.id)}>
                                                    <Trash /> Remove
                                                </Button>
                                            </div>
                                        ) : (
                                            <div>
                                                <Form.Control
                                                    size="sm"
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleImageUpload(field.id, e.target.files[0])}
                                                    disabled={uploadingImageFieldId === field.id}
                                                />
                                                {uploadingImageFieldId === field.id && (
                                                    <div className="mt-1">
                                                        <Spinner animation="border" size="sm" /> Uploading...
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Caption</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            as="textarea"
                                            rows={2}
                                            placeholder="Enter caption text to display below the image"
                                            value={fieldImageConfigs[field.id]?.caption || ''}
                                            onChange={(e) => setFieldImageConfigs(prev => ({
                                                ...prev,
                                                [field.id]: { ...prev[field.id], caption: e.target.value }
                                            }))}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        );
                    })}

                    {/* Gallery configuration for selected gallery fields */}
                    {selectedFieldIds.map(fieldId => {
                        const field = fields.find(f => f.id === fieldId);
                        if (!field || field.fieldType !== 'gallery') return null;
                        const images = fieldGalleryConfigs[field.id]?.images || [];
                        const layout = fieldGalleryConfigs[field.id]?.layout || 'horizontal';
                        return (
                            <div key={`gallery-config-${field.id}`} className="mt-3 p-3" style={{ background: '#f0f7ff', borderRadius: 4, border: '1px solid #b8daff' }}>
                                <strong>Gallery Configuration: {field.title}</strong>

                                {/* Layout selector */}
                                <Form.Group className="mt-2 mb-3">
                                    <Form.Label className="small">Layout</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={layout}
                                        onChange={(e) => setFieldGalleryConfigs(prev => ({
                                            ...prev,
                                            [field.id]: { ...prev[field.id], layout: e.target.value }
                                        }))}
                                    >
                                        <option value="horizontal">Horizontal (side by side)</option>
                                        <option value="vertical">Vertical (stacked)</option>
                                        <option value="grid">Grid</option>
                                    </Form.Select>
                                </Form.Group>

                                {/* Display existing images with captions */}
                                {images.length > 0 && (
                                    <div className="mb-3">
                                        <Form.Label className="small">Gallery Images ({images.length})</Form.Label>
                                        {images.map((img, idx) => {
                                            const fullUrl = (img.url || img).startsWith('http') ? (img.url || img) : BACKEND_URL + (img.url || img);
                                            return (
                                                <div key={idx} className="d-flex align-items-start mb-2 p-2" style={{ background: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                                                    <div style={{ position: 'relative', marginRight: '10px', flexShrink: 0 }}>
                                                        <img
                                                            src={fullUrl}
                                                            alt={`Gallery ${idx + 1}`}
                                                            style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                                                            onError={(e) => e.target.style.display = 'none'}
                                                        />
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="Enter caption for this image"
                                                            value={img.caption || ''}
                                                            onChange={(e) => updateGalleryImageCaption(field.id, idx, e.target.value)}
                                                        />
                                                    </div>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="ms-2"
                                                        onClick={() => removeGalleryImage(field.id, idx)}
                                                    >
                                                        <Trash />
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Upload new image */}
                                <Form.Group className="mb-2">
                                    <Form.Label className="small">Add Image to Gallery</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            handleGalleryImageUpload(field.id, e.target.files[0]);
                                            e.target.value = '';
                                        }}
                                        disabled={uploadingGalleryFieldId === field.id}
                                    />
                                    {uploadingGalleryFieldId === field.id && (
                                        <div className="mt-1">
                                            <Spinner animation="border" size="sm" /> Uploading...
                                        </div>
                                    )}
                                </Form.Group>
                            </div>
                        );
                    })}

                    {/* Static text configuration for selected statictext fields */}
                    {selectedFieldIds.map(fieldId => {
                        const field = fields.find(f => f.id === fieldId);
                        if (!field || field.fieldType !== 'statictext') return null;
                        const html = fieldStaticTextConfigs[field.id]?.html || '';
                        return (
                            <div key={`statictext-config-${field.id}`} className="mt-3 p-3" style={{ background: '#fff8e1', borderRadius: 4, border: '1px solid #ffe082' }}>
                                <strong>Static Text Content: {field.title}</strong>
                                <Form.Text className="d-block text-muted mb-2">
                                    This content will be displayed as-is in the survey, with no input field.
                                </Form.Text>
                                <ReactQuill
                                    theme="snow"
                                    value={html}
                                    onChange={(value) => setFieldStaticTextConfigs(prev => ({
                                        ...prev,
                                        [field.id]: { ...prev[field.id], html: value }
                                    }))}
                                />
                            </div>
                        );
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFieldsModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={saveFieldSelection}>Save</Button>
                </Modal.Footer>
            </Modal>

            {/* Responses Modal */}
            <Modal show={showResponsesModal} onHide={() => setShowResponsesModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Survey Responses</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {responses.length === 0 ? (
                        <p>No responses yet.</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>Submitted</th>
                                        {currentSurvey?.survey_fields?.filter(f => f.fieldType !== 'statictext').map(field => (
                                            <th key={field.id}>{field.title}</th>
                                        ))}
                                        {!isReadOnly && <th>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {responses.map(response => (
                                        <tr key={response.id}>
                                            <td>{new Date(response.submittedAt).toLocaleString()}</td>
                                            {currentSurvey?.survey_fields?.filter(f => f.fieldType !== 'statictext').map(field => {
                                                // For image fields, show the image and caption
                                                if (field.fieldType === 'image') {
                                                    const imageUrl = field.survey_survey_fields?.imageUrl;
                                                    const caption = field.survey_survey_fields?.caption;
                                                    const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : BACKEND_URL + imageUrl) : null;
                                                    return (
                                                        <td key={field.id} style={{ textAlign: 'center', minWidth: '100px' }}>
                                                            {fullImageUrl && (
                                                                <img
                                                                    src={fullImageUrl}
                                                                    alt={field.title}
                                                                    style={{ maxWidth: '80px', maxHeight: '60px', borderRadius: '4px' }}
                                                                />
                                                            )}
                                                            {caption && <div className="small text-muted">{caption.length > 30 ? caption.substring(0, 30) + '...' : caption}</div>}
                                                        </td>
                                                    );
                                                }
                                                // For gallery fields, show thumbnail count
                                                if (field.fieldType === 'gallery') {
                                                    const galleryImagesRaw = field.survey_survey_fields?.galleryImages;
                                                    const parsedImages = galleryImagesRaw ? (typeof galleryImagesRaw === 'string' ? JSON.parse(galleryImagesRaw) : galleryImagesRaw) : [];
                                                    const images = parsedImages.map(img => typeof img === 'string' ? { url: img, caption: '' } : img);
                                                    const firstImage = images[0];
                                                    const firstUrl = firstImage?.url || firstImage;
                                                    const fullUrl = firstUrl ? (firstUrl.startsWith('http') ? firstUrl : BACKEND_URL + firstUrl) : null;
                                                    return (
                                                        <td key={field.id} style={{ textAlign: 'center', minWidth: '100px' }}>
                                                            {fullUrl && (
                                                                <img
                                                                    src={fullUrl}
                                                                    alt="Gallery"
                                                                    style={{ maxWidth: '60px', maxHeight: '45px', borderRadius: '4px' }}
                                                                />
                                                            )}
                                                            <div className="small text-muted">{images.length} image{images.length !== 1 ? 's' : ''}</div>
                                                        </td>
                                                    );
                                                }
                                                const rf = response.survey_response_fields?.find(rf => rf.surveyFieldId === field.id);
                                                let value = rf?.value || '-';
                                                // For multi-select fields, parse and join the array
                                                if (field.fieldType === 'radio' && field.selectType === 'multi' && value.startsWith('[')) {
                                                    try {
                                                        const arr = JSON.parse(value);
                                                        value = arr.join(', ');
                                                    } catch (e) {
                                                        // Keep original value if parse fails
                                                    }
                                                }
                                                // Truncate long values for display
                                                const displayValue = value.length > 50 ? value.substring(0, 50) + '...' : value;
                                                return <td key={field.id} title={value}>{displayValue}</td>;
                                            })}
                                            {!isReadOnly && (
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <Button variant="link" size="sm" onClick={() => openEditResponseModal(response)}>
                                                        <Pencil />
                                                    </Button>
                                                    <Button variant="link" size="sm" className="text-danger" onClick={() => deleteResponseHandler(response.id)}>
                                                        <Trash />
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {responses.length > 0 && (
                        <Button variant="success" onClick={downloadResponsesExcel}>
                            <Download /> Download Excel
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setShowResponsesModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Response Modal */}
            <Modal show={showEditResponseModal} onHide={() => setShowEditResponseModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Response</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentSurvey?.survey_fields?.filter(f => f.fieldType !== 'statictext').map(field => {
                        // For image fields, show the image and caption (not editable)
                        if (field.fieldType === 'image') {
                            const imageUrl = field.survey_survey_fields?.imageUrl;
                            const caption = field.survey_survey_fields?.caption;
                            const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : BACKEND_URL + imageUrl) : null;
                            return (
                                <Form.Group className="mb-3" key={field.id}>
                                    <Form.Label>{field.title}</Form.Label>
                                    <div style={{ textAlign: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                                        {fullImageUrl && (
                                            <img
                                                src={fullImageUrl}
                                                alt={field.title}
                                                style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '4px' }}
                                            />
                                        )}
                                        {caption && <p className="small text-muted mt-2 mb-0">{caption}</p>}
                                        <Form.Text className="text-muted d-block mt-2">Image fields are display-only</Form.Text>
                                    </div>
                                </Form.Group>
                            );
                        }
                        // For gallery fields, show thumbnails with captions (not editable)
                        if (field.fieldType === 'gallery') {
                            const galleryImagesRaw = field.survey_survey_fields?.galleryImages;
                            const parsedImages = galleryImagesRaw ? (typeof galleryImagesRaw === 'string' ? JSON.parse(galleryImagesRaw) : galleryImagesRaw) : [];
                            const images = parsedImages.map(img => typeof img === 'string' ? { url: img, caption: '' } : img);
                            return (
                                <Form.Group className="mb-3" key={field.id}>
                                    <Form.Label>{field.title}</Form.Label>
                                    <div style={{ padding: '10px', background: '#f0f7ff', borderRadius: '4px' }}>
                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                            {images.map((img, idx) => {
                                                const imgUrl = img.url || img;
                                                const fullUrl = imgUrl.startsWith('http') ? imgUrl : BACKEND_URL + imgUrl;
                                                return (
                                                    <div key={idx} style={{ textAlign: 'center' }}>
                                                        <img
                                                            src={fullUrl}
                                                            alt={img.caption || `Gallery ${idx + 1}`}
                                                            style={{ maxWidth: '100px', maxHeight: '75px', borderRadius: '4px', objectFit: 'cover' }}
                                                        />
                                                        {img.caption && <div className="small text-muted">{img.caption}</div>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <Form.Text className="text-muted d-block mt-2 text-center">Gallery fields are display-only</Form.Text>
                                    </div>
                                </Form.Group>
                            );
                        }
                        return (
                            <Form.Group className="mb-3" key={field.id}>
                                <Form.Label>{field.title}</Form.Label>
                                {field.fieldType === 'radio' ? (
                                    field.selectType === 'multi' ? (
                                        // Multi-select checkboxes
                                        <div>
                                            {field.options && JSON.parse(field.options).map((opt, i) => {
                                                const currentValues = editFieldValues[field.id] ?
                                                    (typeof editFieldValues[field.id] === 'string' && editFieldValues[field.id].startsWith('[')
                                                        ? JSON.parse(editFieldValues[field.id])
                                                        : [editFieldValues[field.id]])
                                                    : [];
                                                return (
                                                    <Form.Check
                                                        key={i}
                                                        type="checkbox"
                                                        label={opt}
                                                        checked={currentValues.includes(opt)}
                                                        onChange={(e) => {
                                                            let newValues;
                                                            if (e.target.checked) {
                                                                newValues = [...currentValues, opt];
                                                            } else {
                                                                newValues = currentValues.filter(v => v !== opt);
                                                            }
                                                            setEditFieldValues(prev => ({ ...prev, [field.id]: JSON.stringify(newValues) }));
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        // Single select (radio or dropdown) - use dropdown for editing
                                        <Form.Select
                                            value={editFieldValues[field.id] || ''}
                                            onChange={(e) => setEditFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                        >
                                            <option value="">Select...</option>
                                            {field.options && JSON.parse(field.options).map((opt, i) => (
                                                <option key={i} value={opt}>{opt}</option>
                                            ))}
                                        </Form.Select>
                                    )
                                ) : (
                                    <Form.Control
                                        as={field.fieldType === 'address' || field.fieldType === 'product' ? 'textarea' : 'input'}
                                        type={field.fieldType === 'date' ? 'date' : field.fieldType === 'number' ? 'number' : 'text'}
                                        rows={field.fieldType === 'address' || field.fieldType === 'product' ? 4 : 1}
                                        value={editFieldValues[field.id] || ''}
                                        onChange={(e) => setEditFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                    />
                                )}
                                {(field.fieldType === 'address' || field.fieldType === 'product') && (
                                    <Form.Text className="text-muted">JSON data - edit carefully</Form.Text>
                                )}
                            </Form.Group>
                        );
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditResponseModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={saveResponseEdit}>Save Changes</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminSurveys;

